/**
 * LakehouseDetailPage
 *
 * Detail view for a single lakehouse with tabbed sections.
 * Part of Phase 8: Lakehouse Storage Mapping.
 *
 * Tabs:
 * - Overview: Basic lakehouse information and backup status
 * - Storage Accounts: Manage storage account mappings
 * - Backup History: View past backup runs
 * - Configuration: Backup settings and exclusions
 */

import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams, Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Database } from 'lucide-react';
import { usePageData } from '../../../hooks/usePageData';
import { useAuth } from '../hooks/useAuth';
import { LakehouseStorageList } from '../components/storage/LakehouseStorageList';
import { AddStorageAccountForm } from '../components/storage/AddStorageAccountForm';
import { WorkflowProgressBanner } from '../../workflows/components/WorkflowProgressBanner';
import { GuidedEmptyState } from '../../../components/EmptyState/GuidedEmptyState';
import { useEntityWorkflowStatus } from '../../workflows/hooks/useWorkflowStatus';
import { AppTooltip } from '../../../components/ui/AppTooltip';
import { Modal, Card, Badge, Button, DetailLayout, RefreshButton, Tabs } from '../../../components/ui';
import { PageHelpPanel } from '../../../components/ui/PageHelpPanel';
import { lakehouseDetailHelp, lakehouseFieldTooltips } from '../help/lakehouseDetailHelp';
import { formatGb } from '../../../utils/formatBytes';
import { lakehouseSizeNote } from '../components/common/LakehouseSizeCell';
import { DualScrollTable, usePagination, PaginationControls } from '../../../components/common/TableControls';
import api from '../services/api';
import { formatEastern } from '@/utils/dateFormatter';
import { retiredLabel } from '@/utils/retiredLabel';
import { oneLakeItemUrl } from '../../../utils/oneLake';
import { ProtectionPanel } from '@/components/backup/ProtectionPanel';
import { AccessGate } from '../../../components/access/AccessGate';
import { useHasAccess } from '../../../hooks/useAccess';

// Types
interface Lakehouse {
  id: string;
  fabric_lakehouse_id: string;
  workspace_id: string;
  workspace_name: string;
  name: string;
  description: string | null;
  is_backup_enabled: boolean;
  backup_frequency: string | null;
  schedule_time: string | null;
  schedule_days: string | null;
  retention_days: number | null;
  excluded_tables: string | null;
  configuration_updated_at: string | null;
  last_backup_at: string | null;
  next_scheduled_backup: string | null;
  created_at: string;
  updated_at: string;
  policy_id?: string | null;
  // Additional fields from joined data
  capacity_type?: number;
  capacity_name?: string;
  // Spec 183/186: Fabric/Delta detail surfaced on the Overview tab
  state?: string | null;
  storage_size_gb?: number | null;
  /** Set when the last collection did not size the lakehouse; storage_size_gb is then an old value (Spec 238). */
  size_measurement_error?: string | null;
  /** The last size a snapshot recorded, and its date (T159 item 32); null when none or when measured. */
  sizeLastRecordedGb?: number | null;
  sizeLastRecordedOn?: string | null;
  last_access?: string | null;
  vacuum_retention_days?: number | null;
  sql_endpoint_connection_string?: string | null;
  /** null when the collection has not recorded it. */
  schema_enabled?: boolean | null;
  /** Spec 297 T032: the row's flag and retirement stamp; the page says "retired on" from them. */
  is_active?: boolean;
  deactivated_at?: string | null;
}

// ── Backup History ──────────────────────────────────────────────────────────

interface BackupRun {
  id: string;
  status: string;
  triggeredBy?: string;
  startedAt?: string | Date;
  completedAt?: string | null;
  tablesBackedUp?: number | null;
  bytesTransferred?: number | null;
  errorMessage?: string | null;
}

function LakehouseBackupHistory({ lakehouseId }: { lakehouseId: string }) {
  const [runs, setRuns] = React.useState<BackupRun[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  const { page, setPage, pageSize, changeSize, total, totalPages, pageItems, start } =
    usePagination(runs);

  React.useEffect(() => {
    setLoading(true);
    api
      .get<{ data: BackupRun[] }>(`/api/backups/lakehouses/${lakehouseId}/runs?limit=50`)
      .then((res) => setRuns(res.data ?? []))
      .catch((e) => setError(e?.message ?? 'Failed to load backup history'))
      .finally(() => setLoading(false));
  }, [lakehouseId]);

  const statusColor = (s: string) => {
    const st = (s || '').toLowerCase();
    if (st === 'completed') return 'text-green-600 dark:text-green-400';
    if (st === 'failed') return 'text-red-600 dark:text-red-400';
    if (st === 'running') return 'text-blue-600 dark:text-blue-400';
    return 'text-[var(--color-text-secondary)]';
  };

  if (loading) return (
    <Card className='p-6 text-center text-[var(--color-text-secondary)]'>
      Loading backup history…
    </Card>
  );

  if (error) return (
    <Card className='p-6 text-red-600 dark:text-red-400'>{error}</Card>
  );

  if (runs.length === 0) return (
    <Card className='p-6'>
      <GuidedEmptyState
        icon='calendar'
        title='No Backup History'
        description='This lakehouse has no completed backup runs yet. Use Backup → Storage → Back up now to trigger a run.'
        actionLabel='Go to Backups'
        actionPath='/backup'
        workflowStep='Step 5 of 6: Run First Backup'
      />
    </Card>
  );

  return (
    <Card padding='none' className='overflow-hidden'>
      <DualScrollTable>
      <table className='min-w-full divide-y divide-[var(--color-border)]'>
        <thead className='bg-[var(--color-bg-subtle)]'>
          <tr>
            {['Started', 'Status', 'Type', 'Tables', 'Size', 'Duration'].map((h) => (
              <th key={h} className='px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-[var(--color-text-secondary)]'>{h}</th>
            ))}
          </tr>
        </thead>
        <tbody className='divide-y divide-[var(--color-border)]'>
          {pageItems.map((r) => {
            const start = r.startedAt ? new Date(r.startedAt) : null;
            const end = r.completedAt ? new Date(r.completedAt) : null;
            const dur = start && end ? Math.round((end.getTime() - start.getTime()) / 1000) : null;
            const sizeMB = r.bytesTransferred ? (r.bytesTransferred / 1024 / 1024).toFixed(1) : null;
            return (
              <tr key={r.id} className='hover:bg-[var(--color-bg-subtle)]'>
                <td className='whitespace-nowrap px-4 py-3 text-sm text-[var(--color-text-primary)]'>{start ? formatEastern(start) : '—'}</td>
                <td className={`whitespace-nowrap px-4 py-3 text-sm font-medium ${statusColor(r.status)}`}>{r.status}</td>
                <td className='whitespace-nowrap px-4 py-3 text-sm text-[var(--color-text-secondary)]'>{r.triggeredBy || '—'}</td>
                <td className='whitespace-nowrap px-4 py-3 text-sm text-[var(--color-text-secondary)]'>{r.tablesBackedUp ?? '—'}</td>
                <td className='whitespace-nowrap px-4 py-3 text-sm text-[var(--color-text-secondary)]'>{sizeMB ? `${sizeMB} MB` : '—'}</td>
                <td className='whitespace-nowrap px-4 py-3 text-sm text-[var(--color-text-secondary)]'>{dur !== null ? `${Math.floor(dur / 60)}m ${dur % 60}s` : '—'}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
      </DualScrollTable>
      <PaginationControls
        page={page}
        pageSize={pageSize}
        total={total}
        totalPages={totalPages}
        start={start}
        shown={pageItems.length}
        onPage={setPage}
        onPageSize={changeSize}
      />
    </Card>
  );
}

// Tab types
type TabId = 'overview' | 'storage' | 'backups' | 'config';

interface Tab {
  id: TabId;
  label: string;
  icon: React.ReactNode;
}

// Format date for display
function formatDate(date: string | null | undefined): string {
  if (!date) return 'Never';
  return formatEastern(date);
}

const VALID_TABS: TabId[] = ['overview', 'storage', 'backups', 'config'];

export function LakehouseDetailPage({
  embeddedId,
  onClose,
}: {
  embeddedId?: string;
  onClose?: () => void;
} = {}): React.ReactElement {
  const params = useParams<{ id: string }>();
  const id = embeddedId ?? params.id;
  // When hosted in a modal (embeddedId/onClose provided) we drop the full-page
  // chrome (min-h-screen background + breadcrumb) and close via onClose.
  const embedded = onClose != null;
  const navigate = useNavigate();
  const handleClose = () => (onClose ? onClose() : navigate('/lakehouses'));
  const [searchParams] = useSearchParams();
  const { canAccess } = useAuth();
  const canManage = canAccess('admin');
  // Spec 285 (owner: gate ALL backup functionality): without the backup license only the
  // Overview tab remains, without the protection panel and the backup fields; a ?tab=backups
  // (or storage/config) deep link falls back to Overview.
  const canSeeBackup = useHasAccess('backup');

  // Derive initial tab from ?tab= URL param so workflow banner links land on
  // the right tab even when the component is already mounted.
  const tabFromUrl = searchParams.get('tab') as TabId | null;
  const initialTab: TabId = tabFromUrl && VALID_TABS.includes(tabFromUrl) ? tabFromUrl : 'overview';

  // State
  const [activeTab, setActiveTab] = useState<TabId>(initialTab);
  const [showAddStorage, setShowAddStorage] = useState(false);

  // Sync tab when URL param changes (banner link, back/forward navigation)
  useEffect(() => {
    const tab = searchParams.get('tab') as TabId | null;
    if (tab && VALID_TABS.includes(tab)) {
      setActiveTab(tab);
    }
  }, [searchParams]);

  // Workflow status for progress banner
  // The lakehouse workflow is BACKUP onboarding (storage → policy → schedule → first backup).
  const { data: workflowStatus, isLoading: workflowLoading } = useEntityWorkflowStatus(
    'lakehouse',
    id ?? '',
    !!id && canSeeBackup
  );

  // Tabs configuration
  const allTabs: Tab[] = [
    {
      id: 'overview',
      label: 'Overview',
      icon: (
        <svg className='h-5 w-5' fill='none' viewBox='0 0 24 24' stroke='currentColor'>
          <path
            strokeLinecap='round'
            strokeLinejoin='round'
            strokeWidth={2}
            d='M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z'
          />
        </svg>
      ),
    },
    {
      id: 'storage',
      label: 'Storage Accounts',
      icon: (
        <svg className='h-5 w-5' fill='none' viewBox='0 0 24 24' stroke='currentColor'>
          <path
            strokeLinecap='round'
            strokeLinejoin='round'
            strokeWidth={2}
            d='M4 7v10c0 2.21 3.582 4 8 4s8-1.79 8-4V7M4 7c0 2.21 3.582 4 8 4s8-1.79 8-4M4 7c0-2.21 3.582-4 8-4s8 1.79 8 4'
          />
        </svg>
      ),
    },
    {
      id: 'backups',
      label: 'Backup History',
      icon: (
        <svg className='h-5 w-5' fill='none' viewBox='0 0 24 24' stroke='currentColor'>
          <path
            strokeLinecap='round'
            strokeLinejoin='round'
            strokeWidth={2}
            d='M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z'
          />
        </svg>
      ),
    },
    {
      id: 'config',
      label: 'Configuration',
      icon: (
        <svg className='h-5 w-5' fill='none' viewBox='0 0 24 24' stroke='currentColor'>
          <path
            strokeLinecap='round'
            strokeLinejoin='round'
            strokeWidth={2}
            d='M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z'
          />
          <path
            strokeLinecap='round'
            strokeLinejoin='round'
            strokeWidth={2}
            d='M15 12a3 3 0 11-6 0 3 3 0 016 0z'
          />
        </svg>
      ),
    },
  ];
  const tabs = canSeeBackup ? allTabs : allTabs.filter((t) => t.id === 'overview');
  const effectiveTab: TabId = canSeeBackup ? activeTab : 'overview';

  // Fetch lakehouse details. Spec 226: through usePageData, so a refresh keeps the lakehouse on screen
  // and a failed one shows the Refresh badge; the error view below is for a lakehouse that never loaded.
  const query = useQuery({
    queryKey: ['lakehouse-detail', id],
    queryFn: async () => (await api.get<{ success: boolean; data: Lakehouse }>(`/api/v1/lakehouses/${id}`)).data,
    enabled: !!id,
  });
  const { data: lakehouse, isLoading: loading, error: loadError, refresh, isRefreshing, lastFetchedAt } = usePageData(query);
  const error = lakehouse ? null : loadError
    ? (loadError instanceof Error ? loadError.message : 'Failed to fetch lakehouse details')
    : null;

  // Loading state
  if (loading) {
    return (
      <div className={embedded ? '' : 'min-h-screen bg-[var(--color-bg-page)]'}>
        <div className='mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8'>
          <div className='animate-pulse space-y-6'>
            <div className='h-8 w-1/4 rounded bg-[var(--color-bg-subtle)]'></div>
            <div className='h-4 w-1/2 rounded bg-[var(--color-bg-subtle)]'></div>
            <div className='h-64 rounded bg-[var(--color-bg-subtle)]'></div>
          </div>
        </div>
      </div>
    );
  }

  // Error state
  if (error || !lakehouse) {
    return (
      <div className={embedded ? '' : 'min-h-screen bg-[var(--color-bg-page)]'}>
        <div className='mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8'>
          <Card className='p-6'>
            <div className='text-red-600 dark:text-red-400'>
              <h3 className='text-lg font-medium'>{error || 'Lakehouse not found'}</h3>
              <div className='mt-4'>
                <Button onClick={handleClose}>Back to Lakehouses</Button>
              </div>
            </div>
          </Card>
        </div>
      </div>
    );
  }

  // Render content based on active tab
  const renderTabContent = () => {
    switch (effectiveTab) {
      case 'overview':
        return (
          <div className='space-y-4'>
          {/* 🛑 Protection FIRST, above the descriptive fields. This page previously fetched the
              entity and its run history and nothing else, so "is this actually protected?" — the
              reason most people open it — could not be answered here at all. It is the first
              question, so it goes at the top. */}
          <AccessGate area='backup'>
            <ProtectionPanel entityType='lakehouse' entityId={params.id ?? ''} />
          </AccessGate>
          <Card className='p-6'>
            <h3 className='mb-4 text-lg font-medium text-[var(--color-text-primary)]'>
              Lakehouse Overview
            </h3>
            <dl className='grid grid-cols-1 gap-x-4 gap-y-6 sm:grid-cols-2'>
              <div>
                <dt className='text-sm font-medium text-[var(--color-text-secondary)]'>Name</dt>
                <dd className='mt-1 text-sm text-[var(--color-text-primary)]'>{lakehouse.name}</dd>
              </div>
              <div>
                <dt className='text-sm font-medium text-[var(--color-text-secondary)]'>Workspace</dt>
                <dd className='mt-1 text-sm text-[var(--color-text-primary)]'>
                  {lakehouse.workspace_name}
                </dd>
              </div>
              {canSeeBackup && (
              <div>
                <dt className='text-sm font-medium text-[var(--color-text-secondary)]'>
                  Backup Status
                </dt>
                <dd className='mt-1'>
                  <AppTooltip title={lakehouseFieldTooltips.backupStatus}>
                    <Badge variant={lakehouse.is_backup_enabled ? 'success' : 'default'}>
                      {lakehouse.is_backup_enabled ? 'Enabled' : 'Disabled'}
                    </Badge>
                  </AppTooltip>
                </dd>
              </div>
              )}
              <div>
                <dt className='text-sm font-medium text-[var(--color-text-secondary)]'>
                  Capacity Type
                </dt>
                <dd className='mt-1 text-sm text-[var(--color-text-primary)]'>
                  <AppTooltip title={lakehouseFieldTooltips.capacityType}>
                    <span>
                      {lakehouse.capacity_type
                        ? `Type ${lakehouse.capacity_type}${lakehouse.capacity_name ? ` (${lakehouse.capacity_name})` : ''}`
                        : 'Not assigned'}
                    </span>
                  </AppTooltip>
                </dd>
              </div>
              {canSeeBackup && (<>
              <div>
                <dt className='text-sm font-medium text-[var(--color-text-secondary)]'>
                  Last Backup
                </dt>
                <dd className='mt-1 text-sm text-[var(--color-text-primary)]'>
                  {formatDate(lakehouse.last_backup_at)}
                </dd>
              </div>
              <div>
                <dt className='text-sm font-medium text-[var(--color-text-secondary)]'>
                  Next Scheduled Backup
                </dt>
                <dd className='mt-1 text-sm text-[var(--color-text-primary)]'>
                  {formatDate(lakehouse.next_scheduled_backup)}
                </dd>
              </div>
              <div>
                <dt className='text-sm font-medium text-[var(--color-text-secondary)]'>
                  Retention Days
                </dt>
                <dd className='mt-1 text-sm text-[var(--color-text-primary)]'>
                  <AppTooltip title={lakehouseFieldTooltips.retentionDays}>
                    <span>{lakehouse.retention_days ?? 'Default'}</span>
                  </AppTooltip>
                </dd>
              </div>
              </>)}
              <div>
                <dt className='text-sm font-medium text-[var(--color-text-secondary)]'>Created</dt>
                <dd className='mt-1 text-sm text-[var(--color-text-primary)]'>
                  {formatDate(lakehouse.created_at)}
                </dd>
              </div>
              <div>
                <dt className='text-sm font-medium text-[var(--color-text-secondary)]'>
                  Fabric State
                </dt>
                <dd className='mt-1 text-sm text-[var(--color-text-primary)]'>
                  {lakehouse.state || 'Unknown'}
                </dd>
              </div>
              <div>
                <dt className='text-sm font-medium text-[var(--color-text-secondary)]'>
                  Storage Size
                </dt>
                <dd className='mt-1 text-sm text-[var(--color-text-primary)]'>
                  {lakehouse.size_measurement_error ? (
                    <AppTooltip title={lakehouseSizeNote(lakehouse.size_measurement_error, lakehouse.sizeLastRecordedGb, lakehouse.sizeLastRecordedOn)}>
                      <span className='text-[var(--color-text-secondary)]' data-testid='size-not-measured'>Not measured</span>
                    </AppTooltip>
                  ) : (
                    formatGb(lakehouse.storage_size_gb)
                  )}
                </dd>
              </div>
              <div>
                <dt className='text-sm font-medium text-[var(--color-text-secondary)]'>
                  Last Access
                </dt>
                <dd className='mt-1 text-sm text-[var(--color-text-primary)]'>
                  {formatDate(lakehouse.last_access)}
                </dd>
              </div>
              <div>
                <dt className='text-sm font-medium text-[var(--color-text-secondary)]'>
                  Vacuum Retention (days)
                </dt>
                <dd className='mt-1 text-sm text-[var(--color-text-primary)]'>
                  {lakehouse.vacuum_retention_days ?? 'Default'}
                </dd>
              </div>
              <div>
                <dt className='text-sm font-medium text-[var(--color-text-secondary)]'>Schemas</dt>
                <dd className='mt-1 text-sm text-[var(--color-text-primary)]'>
                  {lakehouse.schema_enabled == null
                    ? 'Not collected'
                    : lakehouse.schema_enabled
                      ? 'Enabled'
                      : 'Not enabled'}
                </dd>
              </div>
              <div className='sm:col-span-2'>
                <dt className='text-sm font-medium text-[var(--color-text-secondary)]'>SQL endpoint</dt>
                <dd className='mt-1 break-all font-mono text-[0.8rem] text-[var(--color-text-primary)]'>
                  {lakehouse.sql_endpoint_connection_string || 'Not collected'}
                </dd>
              </div>
              <div className='sm:col-span-2'>
                <dt className='text-sm font-medium text-[var(--color-text-secondary)]'>OneLake path</dt>
                <dd className='mt-1 break-all font-mono text-[0.8rem] text-[var(--color-text-primary)]'>
                  {oneLakeItemUrl(lakehouse.workspace_id, lakehouse.fabric_lakehouse_id) ?? '—'}
                </dd>
              </div>
              {lakehouse.description && (
                <div className='sm:col-span-2'>
                  <dt className='text-sm font-medium text-[var(--color-text-secondary)]'>
                    Description
                  </dt>
                  <dd className='mt-1 text-sm text-[var(--color-text-primary)]'>
                    {lakehouse.description}
                  </dd>
                </div>
              )}
            </dl>
          </Card>
          </div>
        );

      case 'storage':
        return <LakehouseStorageList lakehouseId={id!} onAddNew={() => setShowAddStorage(true)} />;

      case 'backups':
        return <LakehouseBackupHistory lakehouseId={id!} />;

      case 'config':
        return (
          <Card className='space-y-6 p-6'>
              {/* Spec 201: backup is governed centrally by Backup Plans — managed in the
                  Backup section, not per-entity here. Status shown read-only + manage links. */}
            <div className='flex items-center justify-between rounded-[var(--radius-lg)] border border-[var(--color-border)] bg-[var(--color-bg-subtle)] p-4'>
              <div>
                <h4 className='text-sm font-medium text-[var(--color-text-primary)]'>Backup protection</h4>
                <p className='mt-0.5 text-xs text-[var(--color-text-secondary)]'>
                  Managed centrally by Backup Plans. Assign a destination, plan, and schedule in the Backup section.
                </p>
              </div>
              <div className='flex items-center gap-2'>
                <Badge variant={lakehouse?.is_backup_enabled ? 'success' : 'default'}>
                  {lakehouse?.is_backup_enabled ? 'Protected' : 'Not protected'}
                </Badge>
                <Link to='/backup/storage'><Button variant='outline' size='sm'>Storage</Button></Link>
                <Link to='/backup/plans'><Button variant='outline' size='sm'>Plans</Button></Link>
                <Link to='/backup/schedules'><Button variant='outline' size='sm'>Schedules</Button></Link>
              </div>
            </div>

            <div>
            <h3 className='mb-4 text-lg font-medium text-[var(--color-text-primary)]'>
              Backup Configuration
            </h3>
            <dl className='grid grid-cols-1 gap-x-4 gap-y-6 sm:grid-cols-2'>
              <div>
                <dt className='text-sm font-medium text-[var(--color-text-secondary)]'>
                  Backup Frequency
                </dt>
                <dd className='mt-1 text-sm text-[var(--color-text-primary)]'>
                  {lakehouse.backup_frequency || 'Not configured'}
                </dd>
              </div>
              <div>
                <dt className='text-sm font-medium text-[var(--color-text-secondary)]'>
                  Schedule Time
                </dt>
                <dd className='mt-1 text-sm text-[var(--color-text-primary)]'>
                  {lakehouse.schedule_time || 'Not configured'}
                </dd>
              </div>
              <div>
                <dt className='text-sm font-medium text-[var(--color-text-secondary)]'>
                  Schedule Days
                </dt>
                <dd className='mt-1 text-sm text-[var(--color-text-primary)]'>
                  {lakehouse.schedule_days || 'Not configured'}
                </dd>
              </div>
              <div>
                <dt className='text-sm font-medium text-[var(--color-text-secondary)]'>
                  Configuration Updated
                </dt>
                <dd className='mt-1 text-sm text-[var(--color-text-primary)]'>
                  {formatDate(lakehouse.configuration_updated_at)}
                </dd>
              </div>
              {lakehouse.excluded_tables && (
                <div className='sm:col-span-2'>
                  <dt className='text-sm font-medium text-[var(--color-text-secondary)]'>
                    Excluded Tables
                  </dt>
                  <dd className='mt-1 text-sm text-[var(--color-text-primary)]'>
                    <ul className='list-inside list-disc'>
                      {lakehouse.excluded_tables.split(',').map((table, index) => (
                        <li key={index}>{table.trim()}</li>
                      ))}
                    </ul>
                  </dd>
                </div>
              )}
            </dl>
            {canManage && (
              <div className='mt-6 border-t border-[var(--color-border)] pt-4'>
                <Link
                  to={`/lakehouses?edit=${id}`}
                  className='inline-flex items-center rounded-[var(--radius-md)] bg-[var(--color-accent)] px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-[var(--color-accent-hover)]'
                >
                  Edit Configuration
                </Link>
              </div>
            )}
            </div>
          </Card>
        );

      default:
        return null;
    }
  };

  // Add Storage Account Modal — canonical kit Modal (Spec 197 design blueprint):
  // transparent-but-blocking backdrop, framed, movable, no outside-close.
  const renderAddStorageModal = () => {
    if (!lakehouse) return null;

    return (
      <Modal
        isOpen={showAddStorage}
        onClose={() => setShowAddStorage(false)}
        title="Add Storage Account"
        size="lg"
      >
        <p className="mb-4 text-sm text-[var(--color-text-secondary)]">
          Select a storage account for backup operations on{' '}
          <span className="font-medium text-[var(--color-text-primary)]">{lakehouse.name}</span>
        </p>
        <AddStorageAccountForm
          lakehouseId={lakehouse.id}
          onSuccess={() => setShowAddStorage(false)}
          onCancel={() => setShowAddStorage(false)}
        />
      </Modal>
    );
  };

  const backupBadge = canSeeBackup ? (
    <Badge variant={lakehouse.is_backup_enabled ? 'success' : 'default'}>
      {lakehouse.is_backup_enabled ? '● Backup Enabled' : '○ Backup Disabled'}
    </Badge>
  ) : null;

  const body = (
    <>
      {/* Workflow Progress Banner (Feature 135) — backup onboarding, so backup-licensed only. */}
      {canSeeBackup && (
        <div className='mb-6'>
          <WorkflowProgressBanner status={workflowStatus ?? null} isLoading={workflowLoading} />
        </div>
      )}

      {/* Tabs */}
      <Tabs
        tabs={tabs.map((tab) => ({ key: tab.id, label: tab.label, icon: tab.icon }))}
        value={effectiveTab}
        onChange={(key) => {
          const tab = tabs.find((t) => t.id === key);
          if (tab) setActiveTab(tab.id);
        }}
        className='mb-6'
      />

      {/* Tab Content */}
      {renderTabContent()}

      {/* Add Storage Modal */}
      {renderAddStorageModal()}
    </>
  );

  // In a list page's modal the page has its own h1, so the name is an h2 here.
  if (embedded) {
    return (
      <div className='px-2 py-1'>
        <div className='mb-6 flex flex-wrap items-start justify-between gap-3'>
          <div className='min-w-0'>
            <h2 className='truncate text-xl font-semibold text-[var(--color-text-primary)]'>{lakehouse.name}</h2>
            <p className='mt-1 text-sm text-[var(--color-text-secondary)]'>{lakehouse.workspace_name}</p>
          </div>
          <div className='flex flex-wrap items-center gap-2'>
            {backupBadge}
            <RefreshButton onRefresh={refresh} isRefreshing={isRefreshing} lastFetchedAt={lastFetchedAt} error={loadError} />
          </div>
        </div>
        {body}
      </div>
    );
  }

  return (
    <DetailLayout
      title={lakehouse.name}
      subtitle={lakehouse.workspace_name}
      badges={retiredLabel(lakehouse) ? <Badge variant='default'>{retiredLabel(lakehouse)}</Badge> : undefined}
      icon={<Database className='h-6 w-6' />}
      backTo='/lakehouses'
      backLabel='Back to Lakehouses'
      actions={<>{backupBadge}<PageHelpPanel content={lakehouseDetailHelp} /></>}
      onRefresh={refresh}
      isRefreshing={isRefreshing}
      lastFetchedAt={lastFetchedAt}
      refreshError={loadError}
    >
      {body}
    </DetailLayout>
  );
}

export default LakehouseDetailPage;
